import { useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  useGetItemDetailQuery,
  useUpdateItemAmountsMutation,
  type IPayrollRunItem,
  type IPayrollRunItemLine,
} from "@/services/(company)/payrollRunsApi";
import { PayrollRunItemLineSourceEnum } from "@/utils/enums/payroll.enum";
import type { IOvertimeRequest } from "@/types/overtime";
import { SaveReasonDialog } from "../edit-item/_components/SaveReasonDialog";

const num = (v: string | number | null | undefined) => {
  const n = Number(v ?? 0);
  return isNaN(n) ? 0 : n;
};

const normalizeLine = (l: IPayrollRunItemLine): IPayrollRunItemLine => ({
  ...l,
  amount: Math.max(0, num(l.amount)),
});

interface IProps {
  runId: number;
  row: IPayrollRunItem;
  approvedOt?: IOvertimeRequest[];
  onClose: () => void;
}

export const OvertimeOverrideDialog = ({
  runId,
  row,
  approvedOt,
  onClose,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const { data, isLoading } = useGetItemDetailQuery(
    { runId, itemId: row.id },
    { refetchOnMountOrArgChange: true },
  );
  const [saveAmounts, { isLoading: isSaving }] = useUpdateItemAmountsMutation();
  const detail = data?.data;

  const existingOtLine = useMemo(
    () =>
      detail?.earnings.find(
        (l) => l.source === PayrollRunItemLineSourceEnum.OVERTIME,
      ) ?? null,
    [detail],
  );

  const [amount, setAmount] = useState("0");
  const [reasonOpen, setReasonOpen] = useState(false);

  useEffect(() => {
    if (!detail) return;
    setAmount(String(num(existingOtLine?.amount)));
  }, [detail, existingOtLine]);

  const approvedHours = (approvedOt ?? []).reduce(
    (sum, req) => sum + num(req.approvedHours ?? req.requestedHours),
    0,
  );

  const handleSaveWithReason = async (reason: string) => {
    if (!detail) return;
    const normalizedEarnings = detail.earnings.map(normalizeLine);
    const otAmount = Math.max(0, num(amount));
    const nextEarnings = existingOtLine
      ? normalizedEarnings.map((l) =>
          l.source === PayrollRunItemLineSourceEnum.OVERTIME &&
          l.id === existingOtLine.id
            ? { ...l, amount: otAmount }
            : l,
        )
      : [
          ...normalizedEarnings,
          {
            name: t("company:payrolls.overtime_manual_override"),
            amount: otAmount,
            isActive: true,
            source: PayrollRunItemLineSourceEnum.OVERTIME,
          } as IPayrollRunItemLine,
        ];

    try {
      await saveAmounts({
        runId,
        itemId: row.id,
        data: {
          earnings: nextEarnings,
          benefits: detail.benefits.map(normalizeLine),
          deductions: detail.deductions.map(normalizeLine),
          reason,
        },
      }).unwrap();
      toast.success(t("company:payrolls.overtime_override_saved"));
      setReasonOpen(false);
      onClose();
    } catch (e: any) {
      toast.error(
        e?.data?.message ??
          e?.message ??
          t("company:payrolls.failed_to_save_override"),
      );
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("company:payrolls.manual_overtime_override")}
            </DialogTitle>
            <DialogDescription>
              {t("company:payrolls.overtime_override_description", {
                name: row.employee.fullName,
              })}
            </DialogDescription>
          </DialogHeader>

          {isLoading || !detail ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {approvedHours > 0 && (
                <div className="flex items-start gap-2 rounded-xs border border-warning-muted bg-warning-muted/40 px-3 py-2 text-xs text-warning">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {t(
                      "company:payrolls.approved_overtime_found_not_auto_included",
                      { hours: approvedHours },
                    )}
                  </span>
                </div>
              )}
              <label className="flex flex-col gap-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  {t("company:payrolls.overtime_amount_with_currency", {
                    currency: row.currencyCode,
                  })}
                </span>
                <input
                  type="number"
                  min={0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="h-10 border border-input rounded-xs px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
                />
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              {t("common:buttons.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => setReasonOpen(true)}
              disabled={isLoading || !detail || isSaving}
            >
              {t("company:payrolls.save_override")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SaveReasonDialog
        open={reasonOpen}
        isSaving={isSaving}
        onCancel={() => setReasonOpen(false)}
        onConfirm={handleSaveWithReason}
      />
    </>
  );
};
import { useTranslation } from "react-i18next";
